import api from './client';

export type AlertPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
export type AlertStatus = 'ACTIVE' | 'SNOOZED' | 'DISMISSED' | 'CONFIRMED' | 'RESOLVED';
export type AlertType =
  | 'CONTRACT_EXPIRY'
  | 'DOCUMENT_MISSING'
  | 'EVALUATION_DUE'
  | 'LEAVE_BALANCE'
  | 'BIRTHDAY'
  | 'WORK_ANNIVERSARY'
  | 'CUSTOM';

export interface Alert {
  id: number;
  type: AlertType;
  type_display: string;
  title: string;
  message: string;
  priority: AlertPriority;
  priority_display: string;
  status: AlertStatus;
  status_display: string;
  employee: number | null;
  employee_name: string | null;
  employee_id: number | null;
  contract: number | null;
  contract_number: string | null;
  assigned_to: number | null;
  created_at: string;
  target_date: string | null;
  snooze_until: string | null;
  confirmed_at: string | null;
  confirmed_by: number | null;
  resolved_at: string | null;
  resolved_by: number | null;
  is_read: boolean;
  read_at: string | null;
  read_by: number | null;
  metadata: Record<string, any>;
  days_remaining: number | null;
  is_urgent: boolean;
  is_snoozed_active: boolean;
}

export interface AlertStats {
  total: number;
  unread: number;
  urgent: number;
  by_type: Record<string, { count: number; label: string }>;
  by_priority: Record<string, { count: number; label: string }>;
  by_status: Record<string, { count: number; label: string }>;
}

export interface AlertFilters {
  status?: AlertStatus;
  type?: AlertType;
  priority?: AlertPriority;
  employee?: number;
  is_read?: boolean;
  exclude_snoozed?: boolean;
}

export interface AlertActionRequest {
  action: 'snooze' | 'dismiss' | 'confirm' | 'resolve' | 'mark_read';
  days?: number;
}

// Récupère toutes les alertes avec filtres optionnels
export async function fetchAlerts(filters?: AlertFilters): Promise<Alert[]> {
  const { data } = await api.get<Alert[]>('alerts/', { params: filters });
  return data;
}

// Récupère une alerte spécifique
export async function fetchAlert(id: number): Promise<Alert> {
  const { data } = await api.get<Alert>(`alerts/${id}/`);
  return data;
}

// Récupère les alertes non lues
export async function fetchUnreadAlerts(): Promise<Alert[]> {
  const { data } = await api.get<Alert[]>('alerts/unread/');
  return data;
}

// Récupère les alertes urgentes
export async function fetchUrgentAlerts(): Promise<Alert[]> {
  const { data } = await api.get<Alert[]>('alerts/urgent/');
  return data;
}

// Récupère les statistiques des alertes
export async function fetchAlertStats(): Promise<AlertStats> {
  const { data } = await api.get<AlertStats>('alerts/stats/');
  return data;
}

// Compte le nombre d'alertes non lues
export async function fetchAlertsUnreadCount(): Promise<number> {
  const stats = await fetchAlertStats();
  return stats.unread;
}

// Effectue une action sur une alerte
export async function performAlertAction(
  id: number,
  actionData: AlertActionRequest
): Promise<{ success: boolean; message: string; alert: Alert }> {
  const { data } = await api.post<{ success: boolean; message: string; alert: Alert }>(
    `alerts/${id}/perform_action/`,
    actionData
  );
  return data;
}

// Reporte une alerte
export async function snoozeAlert(id: number, days: number = 1): Promise<Alert> {
  const result = await performAlertAction(id, { action: 'snooze', days });
  return result.alert;
}

// Ignore une alerte
export async function dismissAlert(id: number): Promise<Alert> {
  const result = await performAlertAction(id, { action: 'dismiss' });
  return result.alert;
}

// Confirme une alerte
export async function confirmAlert(id: number): Promise<Alert> {
  const result = await performAlertAction(id, { action: 'confirm' });
  return result.alert;
}

// Résout une alerte
export async function resolveAlert(id: number): Promise<Alert> {
  const result = await performAlertAction(id, { action: 'resolve' });
  return result.alert;
}

// Marque une alerte comme lue
export async function markAlertAsRead(id: number): Promise<Alert> {
  const result = await performAlertAction(id, { action: 'mark_read' });
  return result.alert;
}

// Marque toutes les alertes comme lues
export async function markAllAlertsAsRead(): Promise<{ success: boolean; message: string; count: number }> {
  const { data } = await api.post<{ success: boolean; message: string; count: number }>('alerts/mark_all_read/');
  return data;
}

// Supprime toutes les alertes résolues
export async function clearResolvedAlerts(): Promise<{ success: boolean; message: string; count: number }> {
  const { data } = await api.delete<{ success: boolean; message: string; count: number }>('alerts/clear_resolved/');
  return data;
}

// Crée une nouvelle alerte
export async function createAlert(alert: Partial<Alert>): Promise<Alert> {
  const { data } = await api.post<Alert>('alerts/', alert);
  return data;
}

// Met à jour une alerte
export async function updateAlert(id: number, alert: Partial<Alert>): Promise<Alert> {
  const { data } = await api.put<Alert>(`alerts/${id}/`, alert);
  return data;
}

// Supprime une alerte
export async function deleteAlert(id: number): Promise<void> {
  await api.delete(`alerts/${id}/`);
}

// Compatibilité avec l'ancien code
export async function alertStatusAction(
  id: number,
  action: 'read' | 'ignored' | 'resolved' | 'archived' | 'reactivate'
): Promise<Alert> {
  // Mapping des anciennes actions vers les nouvelles
  const actionMap: Record<string, 'mark_read' | 'dismiss' | 'resolve'> = {
    read: 'mark_read',
    ignored: 'dismiss',
    resolved: 'resolve',
    archived: 'dismiss',
  };

  const newAction = actionMap[action];
  if (!newAction) {
    throw new Error(`Action non supportée: ${action}`);
  }

  const result = await performAlertAction(id, { action: newAction });
  return result.alert;
}

// ===== NOTIFICATIONS PROGRAMMÉES (SCHEDULED NOTIFICATIONS) =====

export type NotificationFrequency = 'ONCE' | 'DAILY' | 'WEEKLY' | 'MONTHLY';
export type NotificationMethod = 'EMAIL' | 'IN_APP' | 'BOTH';
export type NotificationStatus = 'PENDING' | 'ACTIVE' | 'COMPLETED' | 'CANCELLED';

export interface ContractInfo {
  id: number;
  employee_name: string;
  employee_id: number;
  end_date: string;
  contract_type: string;
}

export interface ScheduledNotification {
  id: number;
  contracts: number[];
  contracts_count: number;
  contracts_info: ContractInfo[];
  created_by: number | null;
  created_by_name: string | null;
  scheduled_date: string;
  scheduled_time: string;
  frequency: NotificationFrequency;
  frequency_display: string;
  title: string;
  message: string;
  notification_method: NotificationMethod;
  notification_method_display: string;
  status: NotificationStatus;
  status_display: string;
  created_at: string;
  last_sent_at: string | null;
  next_send_at: string | null;
  sent_count: number;
  metadata: Record<string, any>;
  is_due: boolean;
}

export interface ScheduledNotificationListItem {
  id: number;
  contracts_count: number;
  scheduled_date: string;
  scheduled_time: string;
  frequency: NotificationFrequency;
  frequency_display: string;
  title: string;
  status: NotificationStatus;
  status_display: string;
  created_at: string;
  next_send_at: string | null;
  sent_count: number;
}

export interface CreateScheduledNotificationRequest {
  contract_ids: number[];
  scheduled_date: string;
  scheduled_time: string;
  frequency: NotificationFrequency;
  title?: string;
  message: string;
  notification_method: NotificationMethod;
}

export interface ScheduledNotificationFilters {
  status?: NotificationStatus;
  frequency?: NotificationFrequency;
  created_by_me?: boolean;
}

// Récupère toutes les notifications programmées avec filtres optionnels
export async function fetchScheduledNotifications(
  filters?: ScheduledNotificationFilters
): Promise<ScheduledNotificationListItem[]> {
  const { data } = await api.get<ScheduledNotificationListItem[]>('alerts/scheduled/', { params: filters });
  return data;
}

// Récupère une notification programmée spécifique
export async function fetchScheduledNotification(id: number): Promise<ScheduledNotification> {
  const { data } = await api.get<ScheduledNotification>(`alerts/scheduled/${id}/`);
  return data;
}

// Récupère les notifications programmées actives
export async function fetchActiveScheduledNotifications(): Promise<ScheduledNotificationListItem[]> {
  const { data } = await api.get<ScheduledNotificationListItem[]>('alerts/scheduled/active/');
  return data;
}

// Récupère les notifications programmées qui doivent être envoyées maintenant
export async function fetchDueScheduledNotifications(): Promise<ScheduledNotification[]> {
  const { data } = await api.get<ScheduledNotification[]>('alerts/scheduled/due/');
  return data;
}

// Crée une nouvelle notification programmée
export async function createScheduledNotification(
  notification: CreateScheduledNotificationRequest
): Promise<ScheduledNotification> {
  const { data } = await api.post<ScheduledNotification>('alerts/scheduled/', notification);
  return data;
}

// Met à jour une notification programmée
export async function updateScheduledNotification(
  id: number,
  notification: Partial<CreateScheduledNotificationRequest>
): Promise<ScheduledNotification> {
  const { data } = await api.put<ScheduledNotification>(`alerts/scheduled/${id}/`, notification);
  return data;
}

// Annule une notification programmée
export async function cancelScheduledNotification(
  id: number
): Promise<{ success: boolean; message: string; notification: ScheduledNotification }> {
  const { data } = await api.post<{ success: boolean; message: string; notification: ScheduledNotification }>(
    `alerts/scheduled/${id}/cancel/`
  );
  return data;
}

// Supprime une notification programmée
export async function deleteScheduledNotification(id: number): Promise<void> {
  await api.delete(`alerts/scheduled/${id}/`);
}
